import { useEffect, useState } from 'react';
import { RefreshCw } from 'lucide-react';
import { syncService } from '../services/syncService';

type SyncState = 'synced' | 'offline' | 'pending' | 'error';

export function SyncStatusIndicator() {
  const [state, setState] = useState<SyncState>('synced');
  const [pendingCount, setPendingCount] = useState(0);
  const [isRetrying, setIsRetrying] = useState(false);

  useEffect(() => {
    // Check initial state
    updateSyncState();

    // Listen for online/offline changes
    const unsubOnline = syncService.onOnlineStatusChange((online) => {
      if (!online) {
        setState('offline');
      } else {
        updateSyncState();
      }
    });

    // Listen for sync progress
    const unsubProgress = syncService.onSyncProgress((progress) => {
      if (progress.inProgress) {
        // Don't show syncing state - it's distracting
        // Just update when done
      } else if (progress.failed > 0) {
        setState('error');
      } else {
        updateSyncState();
      }
    });

    // Check for pending items periodically
    const interval = setInterval(updateSyncState, 30000);

    return () => {
      unsubOnline();
      unsubProgress();
      clearInterval(interval);
    };
  }, []);

  const updateSyncState = async () => {
    if (!navigator.onLine) {
      setState('offline');
      return;
    }

    try {
      const stats = await syncService.getSyncStats();
      const total = stats.pending + stats.failed;
      setPendingCount(total);

      if (stats.failed > 0) {
        setState('error');
      } else if (stats.pending > 0) {
        setState('pending');
      } else {
        setState('synced');
      }
    } catch {
      setState('synced');
    }
  };

  const handleRetry = async () => {
    setIsRetrying(true);
    try {
      await syncService.syncAll();
      await updateSyncState();
    } finally {
      setIsRetrying(false);
    }
  };

  // Show nothing when synced - no visual noise
  if (state === 'synced') {
    return null;
  }

  if (state === 'offline') {
    return (
      <div className="flex items-center gap-2 px-3 py-1.5 bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 rounded-full text-sm font-medium">
        <div className="w-2 h-2 bg-amber-500 rounded-full" />
        <span>Offline</span>
      </div>
    );
  }

  if (state === 'pending') {
    return (
      <div className="flex items-center gap-2 px-3 py-1.5 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 rounded-full text-sm font-medium">
        <div className="w-2 h-2 bg-blue-500 rounded-full" />
        <span>{pendingCount} pending</span>
      </div>
    );
  }

  if (state === 'error') {
    return (
      <button
        onClick={handleRetry}
        disabled={isRetrying}
        className="flex items-center gap-2 px-3 py-1.5 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 rounded-full text-sm font-medium hover:bg-red-200 dark:hover:bg-red-900/50 transition-colors"
      >
        <RefreshCw
          className={`w-3.5 h-3.5 ${isRetrying ? 'animate-spin' : ''}`}
        />
        <span>{isRetrying ? 'Retrying...' : 'Sync failed'}</span>
      </button>
    );
  }

  return null;
}
